import {Alert, Button, Card, Checkbox, Col, Form, Input, notification, Row, Select} from "antd";
import React, {useEffect, useRef, useState} from "react";
import api, {getApiErrorData} from "../../api";
import './style.css';
import {ArrowRightOutlined} from "@ant-design/icons";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useTranslations} from "../../lang";
import {setUtmData} from "../../helpers";
import {getInitialRegistrationCountry, getRegistrationMarketProfile, HARDINESS_ZONES, parseRegistrationPlanId} from "./market";
import {getMarketForLocation} from "../../environment";
import PurchaseDisclosure from "../../Components/PurchaseDisclosure";
import {sanitizeRegistrationAttribution} from "../../analytics";
import SEOHead from "../../Components/SEOHead";

export default function Register() {

    const [notificationApi, contextHolder] = notification.useNotification();
    const {translate, language} = useTranslations()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    let selectedPlanId;
    let invalidPlanId = false;
    try {
        selectedPlanId = parseRegistrationPlanId(searchParams.get('plan_id'));
    } catch (error) {
        invalidPlanId = true;
    }
    const requestedMarket = getMarketForLocation(window.location.hostname, `?${searchParams.toString()}`)
    const [currentStep, setCurrentStep] = React.useState(1);
    const [form] = Form.useForm();
    const selectedCountry = Form.useWatch('country', form);
    const marketProfile = getRegistrationMarketProfile(
        selectedCountry || getInitialRegistrationCountry(requestedMarket),
        language
    );
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [planLoadState, setPlanLoadState] = useState('idle');
    const [planLoadAttempt, setPlanLoadAttempt] = useState(0);
    const [acceptedLegalMarket, setAcceptedLegalMarket] = useState(null);
    const isUSRegistration = marketProfile.market === 'US';
    const signupLanguage = isUSRegistration ? 'en' : language;
    const legalBaseUrl = isUSRegistration
        ? 'https://plucking.app'
        : signupLanguage === 'de'
            ? 'https://gemusegartenplaner.de'
            : 'https://www.oogst.app';
    const signupMetadata = {
        en: {
            title: 'Create your Plucking account | Plucking',
            description: 'Create your free Plucking account and start planning your vegetable garden.',
            keywords: 'vegetable garden planner, garden planning, sowing calendar, Plucking',
        },
        de: {
            title: 'Erstelle dein Oogst.app-Konto | Oogst.app',
            description: 'Erstelle dein kostenloses Oogst.app-Konto und plane deinen Gemüsegarten.',
            keywords: 'Gemüsegartenplaner, Gemüsegarten planen, Aussaatkalender, Oogst.app',
        },
        nl: {
            title: 'Maak je Oogst.app-account aan | Oogst.app',
            description: 'Maak je gratis Oogst.app-account aan en begin met het plannen van je moestuin.',
            keywords: 'moestuinplanner, moestuin plannen, zaaikalender, Oogst.app',
        },
    }[signupLanguage] || {
        title: 'Maak je Oogst.app-account aan | Oogst.app',
        description: 'Maak je gratis Oogst.app-account aan en begin met het plannen van je moestuin.',
        keywords: 'moestuinplanner, moestuin plannen, zaaikalender, Oogst.app',
    };
    const requiresUSPlanDisclosure = Boolean(selectedPlanId && isUSRegistration);
    const registrationPlanId = requestedMarket === marketProfile.market ? selectedPlanId : undefined;
    const consentMarketRef = useRef(marketProfile.market);

    useEffect(() => {
        if (api.isLoggedIn()) {
            navigate("/gardens");
        }
    }, [navigate]);

    useEffect(() => {
        if (consentMarketRef.current === marketProfile.market) return;
        consentMarketRef.current = marketProfile.market;
        setAcceptedLegalMarket(null);
        form.setFieldValue('tos', false);
        setCurrentStep(1);
    }, [form, marketProfile.market]);

    useEffect(() => {
        let active = true;
        setSelectedPlan(null);
        if (!requiresUSPlanDisclosure) {
            setPlanLoadState('idle');
            return () => { active = false; };
        }
        setPlanLoadState('loading');
        api.getAllPlans('US').then(plans => {
            if (!active) return;
            const plan = plans.find(candidate => candidate.id === selectedPlanId);
            if (!plan) throw new Error('Selected plan is unavailable');
            setSelectedPlan(plan);
            setPlanLoadState('loaded');
        }).catch(() => {
            if (!active) return;
            setSelectedPlan(null);
            setPlanLoadState('error');
        });
        return () => { active = false; };
    }, [selectedPlanId, requiresUSPlanDisclosure, planLoadAttempt]);


    // Get UTM data from localStorage
    const getUtmData = () => {
        try {
            const utmData = localStorage.getItem('utm_data');
            if (utmData) {
                const parsed = JSON.parse(utmData);
                // Check if data is less than 30 days old (30 * 24 * 60 * 60 * 1000 ms)
                const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
                if (parsed.timestamp > thirtyDaysAgo) {
                    return sanitizeRegistrationAttribution(parsed);
                }
                // Remove expired UTM data
                localStorage.removeItem('utm_data');
            }
        } catch (error) {
            console.error('Error parsing UTM data from localStorage:', error);
            localStorage.removeItem('utm_data');
        }
        return null;
    };

    const onStep1Continue = async () => {
        if (requiresUSPlanDisclosure && planLoadState !== 'loaded') return;
        if (acceptedLegalMarket !== marketProfile.market) return;
        try {
            // Validate only step 1 fields
            await form.validateFields(['name', 'email', 'password', 'password_confirm', 'tos']);
            setCurrentStep(2);
        } catch (error) {
            // Validation failed, errors will be shown by form
            console.log('Step 1 validation failed:', error);
        }
    };

    const onFinish = (values) => {
        // Keep the paid-registration invariant at the API execution boundary too.
        if (requiresUSPlanDisclosure && (planLoadState !== 'loaded' || !selectedPlan)) return;
        if (!values.tos || acceptedLegalMarket !== marketProfile.market) return;
        const utmData = getUtmData();

        api.register(
            values.email,
            values.name,
            values.password,
            values.company,
            utmData?.utm_source || null,
            utmData?.utm_medium || null,
            utmData?.utm_campaign || null,
            values.country,
            marketProfile.language,
            marketProfile.measurements,
            utmData?.supplier_ids || null,
            values.hardiness_zone || null,
            values.postal_code || null,
            registrationPlanId
        ).then((response) => {
            if (response.token) {
                localStorage.setItem('token', response.token);
                // Clean up UTM data after successful registration
                localStorage.removeItem('utm_data');
                window.location.href = response.url || "/welcome";
            }
        }).catch((error) => {
            if (getApiErrorData(error).detail === "UserAlreadyExists") {
                notificationApi.error({
                    message: translate(`Dit emailadres is al in gebruik!`),
                    description: translate('Probeer in te loggen met dit emailadres of gebruik een ander emailadres.'),
                    placement: 'top',
                    duration: 3
                });
                return
            }

            notificationApi.error({
                message: translate(`Woops! Er is iets misgegaan! 🥕`),
                description: translate('Check of je alles goed hebt ingevuld en probeer het later nog eens. Lukt het nog steeds niet? Neem contact op met ons.'),
                placement: 'top',
                duration: 3
            });
        })
    }


    setUtmData();

    if (invalidPlanId) {
        return (
            <div style={{maxWidth: '720px', margin: '24px auto'}}>
                <Alert
                    type="error"
                    showIcon
                    message={translate('Dit abonnement is niet beschikbaar')}
                    description={translate('Controleer de registratielink en kies opnieuw een abonnement.')}
                />
            </div>
        );
    }

    return (
        <>
            <SEOHead
                title={signupMetadata.title}
                description={signupMetadata.description}
                keywords={signupMetadata.keywords}
                noIndex
                language={signupLanguage}
            />
            <Form
                form={form}
                name="register_form"
                className="register-form"
                initialValues={{
                    remember: true,
                    country: getInitialRegistrationCountry(requestedMarket),
                }}
                onFinish={onFinish}>
                <Row justify="center">
                    {contextHolder}
                    <Col md={12} xs={24}>
                        <Card style={{backgroundColor: 'white', marginBottom: '24px'}}>
                            <div style={{display: currentStep === 1 ? 'block' : 'none'}}>
                                <h1>{translate('Registreer vandaag nog!')}</h1>
                                <p>{translate('Begin vandaag nog met het plannen van jouw moestuin.')}</p>

                                <h2>{translate('Over jou')}</h2>
                                <Form.Item
                                    name="name"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Vul je voor en achternaam in'),
                                        },
                                    ]}
                                >
                                    <Input placeholder={translate('Voor- en achternaam')}/>
                                </Form.Item>
                                <Form.Item
                                    name="email"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Vul een emailadres in'),
                                        },
                                        {
                                            type: 'email',
                                            message: translate('Vul een geldig emailadres in'),
                                        },
                                    ]}
                                >
                                    <Input placeholder={translate('E-mail address')}/>
                                </Form.Item>
                                <h2>{translate('Voor je account')}</h2>
                                <Form.Item
                                    name="password"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Voer een wachtwoord in!'),
                                        },
                                    ]}
                                >
                                    <Input.Password placeholder={translate('Wachtwoord')}/>
                                </Form.Item>
                                <Form.Item
                                    name="password_confirm"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Voer een wachtwoord in!'),
                                        },
                                        ({getFieldValue}) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('password') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error(translate('De wachtwoorden die je hebt ingevuld komen niet overeen!')));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password placeholder={translate('Herhaal wachtwoord')}/>
                                </Form.Item>

                                <Form.Item name={"tos"} valuePropName="checked" rules={[{
                                    required: true,
                                    message: translate('Je moet akkoord gaan met de algemene voorwaarden en privacy policy!')
                                }]}>
                                    <Checkbox onChange={event => setAcceptedLegalMarket(
                                        event.target.checked ? marketProfile.market : null
                                    )}>
                                        {signupLanguage === 'de' ? <>
                                            Ich stimme den <a href={`${legalBaseUrl}/tos`} target="_blank" rel="noreferrer">Allgemeinen Geschäftsbedingungen</a> und der <a href={`${legalBaseUrl}/privacy-policy`} target="_blank" rel="noreferrer">Datenschutzerklärung</a> zu.
                                        </> : <>
                                            {translate('Ik ga akkoord met de ')} <a href={`${legalBaseUrl}/tos`}
                                                target="_blank" rel="noreferrer">{translate('algemene voorwaarden')}</a> {translate('en')}
                                            <a target="_blank" rel="noreferrer"
                                               href={`${legalBaseUrl}/privacy-policy`}>{translate('privacy policy')}</a>.
                                        </>}
                                    </Checkbox>
                                </Form.Item>
                                {requiresUSPlanDisclosure && planLoadState === 'loading' && (
                                    <div role="status">{translate('Abonnement laden...')}</div>
                                )}
                                {requiresUSPlanDisclosure && planLoadState === 'error' && (
                                    <Alert
                                        type="error"
                                        showIcon
                                        message={translate('Abonnement kon niet worden geladen')}
                                        action={<Button onClick={() => setPlanLoadAttempt(attempt => attempt + 1)}>{translate('Opnieuw proberen')}</Button>}
                                    />
                                )}
                                {requiresUSPlanDisclosure && planLoadState === 'loaded' && <PurchaseDisclosure plan={selectedPlan}/>}
                            </div>

                            <div style={{display: currentStep === 2 ? 'block' : 'none'}}>
                                <h1>{translate('Over jouw tuin')}</h1>
                                <p>{translate('Vertel ons meer over je tuin om te beginnen.')}</p>

                                <Form.Item
                                    name="company"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Vul de naam van je tuin in, bijvoorbeeld: "Stef\'s moestuin" of "Tuinderij de brocoli"'),
                                        },
                                    ]}>
                                    <Input placeholder={translate('Naam van je tuin(derij)')}/>
                                </Form.Item>
                                <Form.Item
                                    name="country"
                                    rules={[
                                        {
                                            required: true,
                                            message: translate('Selecteer je land'),
                                        },
                                    ]}>
                                    <Select placeholder={translate('Selecteer je land')}>
                                        <Select.Option value="nl">🇳🇱 {translate('Nederland')}</Select.Option>
                                        <Select.Option value="be">🇧🇪 {translate('België')}</Select.Option>
                                        <Select.Option value="de">🇩🇪 {translate('Duitsland')}</Select.Option>
                                        <Select.Option value="us">🇺🇸 {translate('Verenigde Staten')}</Select.Option>
                                        {/*<Select.Option value="fr">🇫🇷 {translate('Frankrijk')}</Select.Option>*/}
                                        {/*<Select.Option value="gb">🇬🇧 {translate('Verenigd Koninkrijk')}</Select.Option>*/}
                                        {/*<Select.Option value="ca">🇨🇦 {translate('Canada')}</Select.Option>*/}
                                        {/*<Select.Option value="au">🇦🇺 {translate('Australië')}</Select.Option>*/}
                                        {/*<Select.Option value="other">{translate('Anders')}</Select.Option>*/}
                                    </Select>
                                </Form.Item>
                                {marketProfile.requiresHardinessZone && (
                                    <Form.Item
                                        name="postal_code"
                                        label={translate('ZIP code')}
                                        extra={translate('Used privately to estimate local frost dates and crop timing. Not shared with advertising providers.')}
                                        normalize={(value) => value?.trim()}
                                        rules={[
                                            {required: true, message: translate('Enter your five-digit ZIP code')},
                                            {pattern: /^\d{5}$/, message: translate('Enter a valid five-digit ZIP code')},
                                        ]}
                                    >
                                        <Input
                                            inputMode="numeric"
                                            autoComplete="postal-code"
                                            maxLength={5}
                                            placeholder="e.g. 02108"
                                            data-sentry-mask="true"
                                            data-private="true"
                                        />
                                    </Form.Item>
                                )}
                                {marketProfile.requiresHardinessZone && (
                                    <Form.Item
                                        name="hardiness_zone"
                                        label={translate('USDA winterhardheidszone')}
                                        rules={[{
                                            required: true,
                                            message: translate('Selecteer je USDA winterhardheidszone'),
                                        }]}
                                    >
                                        <Select placeholder={translate('Selecteer je USDA winterhardheidszone')}>
                                            {HARDINESS_ZONES.map((zone) => (
                                                <Select.Option key={zone} value={zone}>{zone}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                )}
                            </div>
                        </Card>
                    </Col>
                </Row>
                <Row>
                    <Col span={24} className={"text-center"}>
                        {currentStep === 1 && (
                            <Form.Item>
                                <Button type="primary" size={"large"} onClick={onStep1Continue}
                                        disabled={requiresUSPlanDisclosure && planLoadState !== 'loaded'}
                                        className="login-form-button">
                                    {translate('Volgende')} <ArrowRightOutlined/>
                                </Button>
                            </Form.Item>
                        )}
                        {currentStep === 2 && (
                            <>
                                <Form.Item style={{display: 'inline-block', marginRight: '12px'}}>
                                    <Button size={"large"} onClick={() => setCurrentStep(1)}>
                                        {translate('Terug')}
                                    </Button>
                                </Form.Item>
                                <Form.Item style={{display: 'inline-block'}}>
                                    <Button type="primary" size={"large"} htmlType="submit" className="login-form-button">
                                        {translate('Aanmelden en direct beginnen')} <ArrowRightOutlined/>
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Col>
                </Row>

                <Row>
                    <Col span={24} className={"text-center"}>
                        <Button type={"link"}
                                onClick={() => navigate('/login')}>{translate('Terug naar inloggen')}</Button>
                    </Col>
                </Row>
            </Form>
        </>
    );
}
